import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Data table primitives — codify the DESIGN.md table spec so page-level
 * markup can't drift.
 *
 * The spec (see DESIGN.md → Components → Tables):
 *   - 1px `--border` row separators (no zebra — collides with hover signal).
 *   - Header row: `text-xs uppercase tracking-wide text-muted-foreground`,
 *     NO background tint, NO bottom border — the data row borders carry the
 *     visual weight.
 *   - Hover row: `hover:bg-accent/40` with `transition-colors`.
 *   - Numeric cells: right-aligned, `tabular-nums` (already global via
 *     globals.css on every <td>).
 *   - Code cells: `font-mono text-xs` for IDs / niche codes / team codes.
 *   - Never wrap in `<Card>`: the scroll container is the frame. At sm+
 *     `.table-frame` draws it as an Obsidian panel; on phones the
 *     responsive-stack cards stand alone.
 *
 * These primitives compose with the `responsive-stack` mobile recipe in
 * globals.css — `data-label="…"` attributes on <DataTableCell> continue to
 * work below `sm`.
 */

interface DataTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /**
   * Wraps the table in `overflow-x-auto` so wide column sets scroll
   * horizontally instead of blowing out the layout. Default true.
   */
  scroll?: boolean;
  /**
   * Density tuning. Admin surfaces use `admin` (px-3 py-2 cells); outreacher
   * surfaces use `outreacher` (px-4 py-3). Defaults to `admin` — the majority
   * of tables in this portal are admin-facing.
   */
  density?: 'admin' | 'outreacher';
  /**
   * Keep the header row visible while the rows scroll (sm+). Default true;
   * only applies with `scroll`. See `useStickyHeader`.
   */
  stickyHeader?: boolean;
}

const DensityContext = React.createContext<'admin' | 'outreacher'>('admin');

/**
 * Root <table> — applies `responsive-stack` so the mobile card recipe kicks
 * in below `sm`, `whitespace-nowrap` so cell content doesn't reflow inside a
 * scroll container, and `text-sm` as the row body default.
 */
/** Nearest ancestor that scrolls (or clips) — what a sticky header sticks to. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(p);
    if (/(auto|scroll|hidden)/.test(overflowY) || /(auto|scroll|hidden)/.test(overflowX)) return p;
  }
  return null;
}

/**
 * Sticky header rows. A sticky <th> sticks to its nearest scroll container,
 * and the frame's `overflow-x: auto` would make that the frame itself (which
 * never scrolls vertically), so the header would never stick. Two modes:
 *
 *   - `page`: the table fits its frame, so the frame clips instead of
 *     scrolling and the header sticks to the page, just under the app header.
 *   - `frame`: the table is wider than its frame, so the frame keeps its
 *     horizontal scroll and also scrolls vertically within the viewport,
 *     with the header pinned to its top.
 *
 * Inside a scrolling panel (discovery's full-bleed workspace) the offset is
 * 0 rather than the app header's height.
 */
function useStickyHeader(frameRef: React.RefObject<HTMLDivElement | null>, enabled: boolean): void {
  React.useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!enabled || !frame || typeof ResizeObserver === 'undefined') return;
    const table = frame.querySelector('table');
    if (!table) return;
    const update = (): void => {
      const fits = table.scrollWidth <= frame.clientWidth + 1;
      const inPanel = scrollParent(frame) !== null;
      const header = document.querySelector<HTMLElement>('.app-header');
      const top = inPanel ? 0 : (header?.getBoundingClientRect().height ?? 0);
      frame.dataset.stick = fits ? 'page' : 'frame';
      frame.style.setProperty('--table-sticky-top', `${top}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(frame);
    ro.observe(table);
    return () => {
      ro.disconnect();
      delete frame.dataset.stick;
    };
  }, [frameRef, enabled]);
}

export function DataTable({
  scroll = true,
  density = 'admin',
  stickyHeader = true,
  className,
  children,
  ...props
}: DataTableProps): JSX.Element {
  const frameRef = React.useRef<HTMLDivElement>(null);
  useStickyHeader(frameRef, scroll && stickyHeader);
  const table = (
    <table
      className={cn(
        'responsive-stack w-full whitespace-nowrap text-sm',
        className,
      )}
      {...props}
    >
      {children}
    </table>
  );
  const inner = (
    <DensityContext.Provider value={density}>{table}</DensityContext.Provider>
  );
  if (!scroll) return inner;
  return (
    <div ref={frameRef} className="table-frame relative overflow-x-auto">
      {inner}
    </div>
  );
}

/**
 * <thead> wrapper. Just the styled row-container — the actual cells are
 * <DataTableHeaderCell>. Renders no background and no bottom border.
 */
export function DataTableHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>): JSX.Element {
  return (
    <thead className={cn('text-left', className)} {...props}>
      {children}
    </thead>
  );
}

interface DataTableHeaderCellProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Right-align + emphasize numeric column headers so they read as a group. */
  numeric?: boolean;
  /** Reserved narrow column (e.g. expand chevron, actions). Applies `w-6`. */
  narrow?: boolean;
}

export function DataTableHeaderCell({
  numeric,
  narrow,
  className,
  ...props
}: DataTableHeaderCellProps): JSX.Element {
  const density = React.useContext(DensityContext);
  return (
    <th
      className={cn(
        'text-xs font-semibold text-muted-foreground/80',
        density === 'outreacher' ? 'px-4 py-3' : 'px-3 py-2.5',
        numeric && 'text-right',
        narrow && 'w-6',
        className,
      )}
      {...props}
    />
  );
}

interface DataTableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  /**
   * Visual state.
   *  - `default`: standard row.
   *  - `expanded`: row currently opened with a detail panel below.
   *  - `terminal`: rejected / archived / dimmed row (opacity 60%).
   *  - `emphasis`: highlighted (e.g. the row whose detail sheet is open).
   *  - `selected`: ticked for a bulk action — brand tint plus a left bar, so
   *    it reads differently from the hover tint.
   */
  state?: 'default' | 'expanded' | 'terminal' | 'emphasis' | 'selected';
  /**
   * Suppresses the top border. Use on the FIRST row inside `<tbody>` when the
   * header row is absent (empty-state rendered above a header-less table).
   * Rarely needed — default is a border-t on every row.
   */
  noBorder?: boolean;
}

/**
 * Data row — border-t separator + hover tint. No zebra ever (banned in
 * DESIGN.md: zebra collides with hover signal).
 */
export function DataTableRow({
  state = 'default',
  noBorder,
  className,
  ...props
}: DataTableRowProps): JSX.Element {
  return (
    <tr
      data-state={state}
      className={cn(
        !noBorder && 'border-t border-border',
        'transition-colors duration-150',
        state === 'expanded' && 'bg-accent/30',
        state === 'terminal' && 'opacity-60',
        state === 'emphasis' && 'bg-accent/40',
        state === 'selected' && 'bg-brand/10 shadow-[inset_3px_0_0_var(--brand)] hover:bg-brand/15',
        // Hover tint applies to every non-expanded row; expanded rows keep
        // their tint through hover so the drawer visually anchors to the row.
        state !== 'expanded' && state !== 'selected' && 'hover:bg-accent/40',
        className,
      )}
      {...props}
    />
  );
}

interface DataTableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** Right-aligned numeric column (money, counts, percentages). */
  numeric?: boolean;
  /** Monospace cell for codes (niche codes, team codes, IDs). */
  code?: boolean;
  /** Muted secondary cell (dates, meta text, low-emphasis columns). */
  muted?: boolean;
  /** Anchor cell — the first cell in the row. Slightly heavier weight so
   *  the row reads with a clear "starts here" hook. */
  anchor?: boolean;
  /** Actions cluster cell. Aligned to the right edge; on mobile becomes a
   *  full-width row footer via the responsive-stack recipe (add `actions`
   *  class automatically). */
  actions?: boolean;
  /** Narrow reserved column (e.g. expand chevron). Matches the header
   *  primitive's `narrow` prop. */
  narrow?: boolean;
  /**
   * Column padding density override. Defaults inherit from <DataTable>.
   */
  density?: 'admin' | 'outreacher';
}

export function DataTableCell({
  numeric,
  code,
  muted,
  anchor,
  actions,
  narrow,
  density: densityOverride,
  className,
  ...props
}: DataTableCellProps): JSX.Element {
  const density = densityOverride ?? React.useContext(DensityContext);
  return (
    <td
      className={cn(
        density === 'outreacher' ? 'px-4 py-3' : 'px-3 py-2',
        'align-middle',
        anchor && 'font-medium',
        muted && 'text-muted-foreground',
        numeric && 'text-right',
        code && 'font-mono text-xs',
        narrow && 'w-6',
        // `actions` also emits the raw `actions` CSS class so the mobile
        // responsive-stack recipe in globals.css picks it up as the row's
        // full-width action footer.
        actions && 'text-right actions',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Row-spanning cell used to embed a detail panel below an expanded row.
 * Renders `<td colSpan={span}>` with `p-0` so the child controls its own
 * padding (typical children: <RowDetailPanel /> in globals.css).
 */
export function DataTableExpandedCell({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>): JSX.Element {
  return (
    <td className={cn('p-0', className)} {...props}>
      {children}
    </td>
  );
}

/**
 * Loading skeleton row. Renders `cols` cells with animated placeholder bars.
 * Widths are Tailwind width utilities so callers can approximate the real
 * column widths and avoid a visible reflow on hydration.
 */
interface DataTableSkeletonProps {
  rows?: number;
  cols: string[];
}

export function DataTableSkeleton({
  rows = 6,
  cols,
}: DataTableSkeletonProps): JSX.Element {
  return (
    <tbody aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-t border-border">
          {cols.map((w, c) => (
            <td key={c} className="px-3 py-2.5">
              <div
                className={cn(
                  'h-3 max-w-full animate-pulse rounded bg-muted-foreground/15 motion-reduce:animate-none',
                  w,
                )}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
